/**
 * Checkout Page
 * Multi-step checkout with shipping, payment, and confirmation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Main, Flex } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton } from '../../../components/styled/Button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../../../contexts/AuthContext';
import orderService from '../services/orderService';
import addressService from '../services/addressService';
import AddressManager from '../components/AddressManager';
import CheckoutStepper from '../components/CheckoutStepper';
import useShopFeatures from '../hooks/useShopFeatures';
import './Shop.css';

const Checkout = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });
  const [processing, setProcessing] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [useNewAddress, setUseNewAddress] = useState(false);
  const [saveAddress, setSaveAddress] = useState(false);
  const { cart, getTotal, clearCart } = useCart();
  const { user } = useAuth();
  const features = useShopFeatures();
  const navigate = useNavigate();

  useEffect(() => {
    if (features.addressBook) {
      loadSavedAddresses();
    }
  }, [features.addressBook]);

  const loadSavedAddresses = async () => {
    try {
      const addresses = await addressService.getAddresses();
      setSavedAddresses(addresses);
      
      // Auto-select default address
      const defaultAddress = addresses.find(addr => addr.isDefault);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
        fillShippingFromAddress(defaultAddress);
      } else if (addresses.length > 0) {
        setUseNewAddress(false);
      } else {
        setUseNewAddress(true);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      setUseNewAddress(true);
    }
  };

  const fillShippingFromAddress = (address) => {
    setShippingInfo({
      fullName: address.fullName,
      address: `${address.streetAddress}${address.apartment ? ', ' + address.apartment : ''}`,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      phone: address.phone || ''
    });
    setSelectedAddressId(address.id);
    setUseNewAddress(false);
  };

  const handleAddressSelection = (addressId) => {
    setSelectedAddressId(addressId);
    const selected = savedAddresses.find(addr => addr.id === addressId);
    if (selected) {
      fillShippingFromAddress(selected);
    }
  };

  if (cart.length === 0) {
    navigate('/shop/cart');
    return null;
  }

  const subtotal = getTotal();
  const shipping = subtotal > 50 ? 0 : 9.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    
    // Validate shipping info
    if (!shippingInfo.fullName || !shippingInfo.address || !shippingInfo.city || !shippingInfo.state || !shippingInfo.zipCode) {
      alert('Please fill in all shipping fields');
      return;
    }

    // Save new address if requested
    if (useNewAddress && saveAddress && features.addressBook) {
      try {
        await addressService.createAddress({
          fullName: shippingInfo.fullName,
          streetAddress: shippingInfo.address,
          apartment: '',
          city: shippingInfo.city,
          state: shippingInfo.state,
          zipCode: shippingInfo.zipCode,
          country: 'United States',
          phone: shippingInfo.phone || '',
          isDefault: savedAddresses.length === 0
        });
      } catch (error) {
        console.error('Error saving address:', error);
        // Don't block checkout if address save fails
      }
    }

    setCurrentStep(2);
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    // Validate payment info (mock)
    if (!paymentInfo.cardNumber || !paymentInfo.cardName || !paymentInfo.expiryDate || !paymentInfo.cvv) {
      alert('Please fill in all payment fields');
      return;
    }
    setCurrentStep(3);
  };

  const handlePlaceOrder = async () => {
    try {
      setProcessing(true);
      
      const orderData = {
        items: cart.map(item => ({
          productId: item.productId,
          name: item.product.name,
          price: item.product.price,
          quantity: item.quantity,
          variation: item.variation
        })),
        shippingAddress: shippingInfo,
        paymentMethod: 'mock',
        total: total
      };

      const result = await orderService.createOrder(orderData);
      
      // Clear cart
      clearCart();
      
      // Navigate to order confirmation
      navigate(`/shop/order/${result.order.id}?success=true`);
      
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Container>
      <Main>
        <div className="shop-page checkout-page">
          <h1>Checkout</h1>
          
          <CheckoutStepper currentStep={currentStep} />

          <div className="checkout-content">
            {/* Step 1: Shipping Information */}
            {currentStep === 1 && (
              <Card>
                <CardContent>
                  <h2>Shipping Information</h2>

                  {/* Saved Addresses Selection */}
                  {features.addressBook && savedAddresses.length > 0 && !useNewAddress && (
                    <div className="saved-addresses-section">
                      <h3>Select Shipping Address</h3>
                      <div className="address-selection">
                        {savedAddresses.map((address) => (
                          <div
                            key={address.id}
                            className={`address-option ${selectedAddressId === address.id ? 'selected' : ''}`}
                            onClick={() => handleAddressSelection(address.id)}
                          >
                            <input
                              type="radio"
                              name="address"
                              value={address.id}
                              checked={selectedAddressId === address.id}
                              onChange={() => handleAddressSelection(address.id)}
                            />
                            <div className="address-content">
                              <strong>{address.label}</strong>
                              {address.isDefault && <span className="default-badge">Default</span>}
                              <p>{address.fullName}</p>
                              <p>{address.addressLine1}{address.addressLine2 ? ', ' + address.addressLine2 : ''}</p>
                              <p>{address.city}, {address.state} {address.postalCode}</p>
                              <p>{address.phone}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <SecondaryButton 
                        onClick={() => setUseNewAddress(true)} 
                        style={{ marginTop: '16px', width: '100%' }}
                      >
                        + Use a Different Address
                      </SecondaryButton>
                    </div>
                  )}

                  {/* New Address Form */}
                  {(useNewAddress || !features.addressBook || savedAddresses.length === 0) && (
                    <form onSubmit={handleShippingSubmit} className="checkout-form">
                      {savedAddresses.length > 0 && (
                        <SecondaryButton 
                          type="button"
                          onClick={() => setUseNewAddress(false)} 
                          style={{ marginBottom: '16px', width: '100%' }}
                        >
                          ← Back to Saved Addresses
                        </SecondaryButton>
                      )}

                      <div className="form-group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          value={shippingInfo.fullName}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, fullName: e.target.value })}
                          required
                          className="form-input"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Address *</label>
                        <input
                          type="text"
                          value={shippingInfo.address}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
                          required
                          className="form-input"
                        />
                      </div>

                      <Flex gap="12px">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>City *</label>
                          <input
                            type="text"
                            value={shippingInfo.city}
                            onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                            required
                            className="form-input"
                          />
                        </div>

                        <div className="form-group" style={{ flex: 1 }}>
                          <label>State *</label>
                          <input
                            type="text"
                            value={shippingInfo.state}
                            onChange={(e) => setShippingInfo({ ...shippingInfo, state: e.target.value })}
                            required
                            className="form-input"
                          />
                        </div>

                        <div className="form-group" style={{ flex: 1 }}>
                          <label>ZIP Code *</label>
                          <input
                            type="text"
                            value={shippingInfo.zipCode}
                            onChange={(e) => setShippingInfo({ ...shippingInfo, zipCode: e.target.value })}
                            required
                            className="form-input"
                          />
                        </div>
                      </Flex>

                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="tel"
                          value={shippingInfo.phone}
                          onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                          className="form-input"
                        />
                      </div>

                      {features.addressBook && (
                        <div className="form-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={saveAddress}
                              onChange={(e) => setSaveAddress(e.target.checked)}
                            />
                            <span>Save this address for future orders</span>
                          </label>
                        </div>
                      )}

                      <PrimaryButton type="submit" style={{ width: '100%', marginTop: '20px' }}>
                        Continue to Payment
                      </PrimaryButton>
                    </form>
                  )}

                  {/* Continue Button for Saved Address Selection */}
                  {features.addressBook && savedAddresses.length > 0 && !useNewAddress && (
                    <PrimaryButton 
                      onClick={handleShippingSubmit} 
                      style={{ width: '100%', marginTop: '20px' }}
                    >
                      Continue to Payment
                    </PrimaryButton>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 2: Payment Information */}
            {currentStep === 2 && (
              <Card>
                <CardContent>
                  <h2>Payment Information (Mock - No Real Charges)</h2>
                  <form onSubmit={handlePaymentSubmit} className="checkout-form">
                    <div className="form-group">
                      <label>Card Number *</label>
                      <input
                        type="text"
                        value={paymentInfo.cardNumber}
                        onChange={(e) => setPaymentInfo({ ...paymentInfo, cardNumber: e.target.value })}
                        placeholder="4111 1111 1111 1111"
                        required
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Name on Card *</label>
                      <input
                        type="text"
                        value={paymentInfo.cardName}
                        onChange={(e) => setPaymentInfo({ ...paymentInfo, cardName: e.target.value })}
                        required
                        className="form-input"
                      />
                    </div>

                    <Flex gap="12px">
                      <div className="form-group" style={{ flex: 1 }}>
                        <label>Expiry Date *</label>
                        <input
                          type="text"
                          value={paymentInfo.expiryDate}
                          onChange={(e) => setPaymentInfo({ ...paymentInfo, expiryDate: e.target.value })}
                          placeholder="MM/YY"
                          required
                          className="form-input"
                        />
                      </div>

                      <div className="form-group" style={{ flex: 1 }}>
                        <label>CVV *</label>
                        <input
                          type="text"
                          value={paymentInfo.cvv}
                          onChange={(e) => setPaymentInfo({ ...paymentInfo, cvv: e.target.value })}
                          placeholder="123"
                          required
                          maxLength="4"
                          className="form-input"
                        />
                      </div>
                    </Flex>

                    <Flex gap="12px" style={{ marginTop: '20px' }}>
                      <SecondaryButton onClick={() => setCurrentStep(1)} style={{ flex: 1 }}>
                        Back
                      </SecondaryButton>
                      <PrimaryButton type="submit" style={{ flex: 2 }}>
                        Review Order
                      </PrimaryButton>
                    </Flex>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Review & Confirm */}
            {currentStep === 3 && (
              <div>
                <Card style={{ marginBottom: '20px' }}>
                  <CardContent>
                    <h2>Review Your Order</h2>
                    
                    {/* Order Items */}
                    <div className="order-review-items">
                      <h3>Items ({cart.length})</h3>
                      {cart.map((item, idx) => (
                        <Flex key={idx} justify="space-between" style={{ padding: '12px 0', borderBottom: '1px solid var(--ui-border)' }}>
                          <div>
                            <strong>{item.product.name}</strong>
                            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                              Qty: {item.quantity} × ${item.product.price.toFixed(2)}
                            </div>
                          </div>
                          <div>
                            ${(item.product.price * item.quantity).toFixed(2)}
                          </div>
                        </Flex>
                      ))}
                    </div>

                    {/* Shipping Address */}
                    <div className="order-review-section">
                      <h3>Shipping Address</h3>
                      <p>
                        {shippingInfo.fullName}<br />
                        {shippingInfo.address}<br />
                        {shippingInfo.city}, {shippingInfo.state} {shippingInfo.zipCode}
                      </p>
                    </div>

                    {/* Payment */}
                    <div className="order-review-section">
                      <h3>Payment Method</h3>
                      <p>Card ending in {paymentInfo.cardNumber.slice(-4)}</p>
                    </div>

                    {/* Order Total */}
                    <div className="order-summary">
                      <Flex justify="space-between">
                        <span>Subtotal:</span>
                        <span>${subtotal.toFixed(2)}</span>
                      </Flex>
                      <Flex justify="space-between">
                        <span>Shipping:</span>
                        <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                      </Flex>
                      <Flex justify="space-between">
                        <span>Tax:</span>
                        <span>${tax.toFixed(2)}</span>
                      </Flex>
                      <div className="summary-divider"></div>
                      <Flex justify="space-between" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                        <span>Total:</span>
                        <span>${total.toFixed(2)}</span>
                      </Flex>
                    </div>

                    <Flex gap="12px" style={{ marginTop: '24px' }}>
                      <SecondaryButton onClick={() => setCurrentStep(2)} style={{ flex: 1 }}>
                        Back
                      </SecondaryButton>
                      <PrimaryButton 
                        onClick={handlePlaceOrder}
                        disabled={processing}
                        style={{ flex: 2 }}
                      >
                        {processing ? 'Processing...' : 'Place Order'}
                      </PrimaryButton>
                    </Flex>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </Main>
    </Container>
  );
};

export default Checkout;

